import React, { useState, useEffect } from 'react'
import {
  X, Save, Database, Users, Calendar, Activity, Settings,
  AlertCircle, Loader
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface Family {
  id: string
  slug: string
  name: string
  plan: string
  status: string
  db_name: string
  member_count?: number
  storage_used_mb?: number
  created_at: string
  updated_at?: string
  last_activity_at?: string
}

interface FamilyDetail extends Family {
  db_host?: string
  db_port?: number
  trial_ends_at?: string
  subscription_ends_at?: string
}

interface FeatureToggle {
  id: string
  feature_key: string
  enabled: boolean
  enabled_at?: string
  enabled_by?: string
  disabled_at?: string
  notes?: string
  display_name?: string
  description?: string
}

interface FamilyMember {
  id: string
  username: string
  display_name: string
  email?: string
  is_parent: boolean
  is_active: boolean
  login_enabled: boolean
  last_login?: string
  created_at: string
}

interface Props {
  family: Family
  onClose: () => void
  onUpdate: () => void
}

export const FamilyDetailModal: React.FC<Props> = ({ family, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [detail, setDetail] = useState<FamilyDetail | null>(null)
  const [status, setStatus] = useState(family.status)
  const [plan, setPlan] = useState(family.plan)
  const [hasChanges, setHasChanges] = useState(false)
  const [features, setFeatures] = useState<FeatureToggle[]>([])
  const [loadingFeatures, setLoadingFeatures] = useState(true)
  const [togglingFeature, setTogglingFeature] = useState<string | null>(null)
  const [members, setMembers] = useState<FamilyMember[]>([])
  const [loadingMembers, setLoadingMembers] = useState(true)

  useEffect(() => {
    loadFamilyDetail()
    loadFeatures()
    loadMembers()
  }, [family.id])

  useEffect(() => {
    setHasChanges(status !== family.status || plan !== family.plan)
  }, [status, plan, family])

  const loadFamilyDetail = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get(`/api/platform/families/${family.id}`)
      setDetail(data.family)
    } catch (error) {
      console.error('Failed to load family details:', error)
      toast.error('Failed to load family details')
    } finally {
      setLoading(false)
    }
  }

  const loadFeatures = async () => {
    setLoadingFeatures(true)
    try {
      const data = await apiClient.get(`/api/platform/families/${family.id}/features`)
      setFeatures(data.features || [])
    } catch (error) {
      console.error('Failed to load features:', error)
      // Don't show error toast - feature toggles might not be available for all families
    } finally {
      setLoadingFeatures(false)
    }
  }

  const loadMembers = async () => {
    setLoadingMembers(true)
    try {
      const data = await apiClient.get(`/api/platform/families/${family.id}/members`)
      setMembers(data.members || [])
    } catch (error) {
      console.error('Failed to load members:', error)
    } finally {
      setLoadingMembers(false)
    }
  }

  const handleToggleFeature = async (featureKey: string, currentlyEnabled: boolean) => {
    setTogglingFeature(featureKey)
    try {
      await apiClient.put(`/api/platform/families/${family.id}/features/${featureKey}`, {
        enabled: !currentlyEnabled,
        notes: `Toggled by platform admin`
      })
      toast.success(`Feature ${currentlyEnabled ? 'disabled' : 'enabled'} successfully`)
      await loadFeatures()
    } catch (error: any) {
      console.error('Failed to toggle feature:', error)
      toast.error(error.response?.data?.error || 'Failed to toggle feature')
    } finally {
      setTogglingFeature(null)
    }
  }

  const handleSave = async () => {
    if (!hasChanges) return

    setSaving(true)
    try {
      // Update via PATCH endpoint
      await apiClient.patch(`/api/platform/families/${family.id}`, {
        status: status !== family.status ? status : undefined,
        plan: plan !== family.plan ? plan : undefined
      })
      toast.success('Family updated successfully')
      onUpdate()
    } catch (error: any) {
      console.error('Failed to update family:', error)
      toast.error(error.response?.data?.error || 'Failed to update family')
    } finally {
      setSaving(false)
    }
  }

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'text-green-600 bg-green-50 border-green-200',
      inactive: 'text-gray-600 bg-gray-50 border-gray-200',
      suspended: 'text-red-600 bg-red-50 border-red-200',
      trial: 'text-blue-600 bg-blue-50 border-blue-200',
    }
    return colors[status as keyof typeof colors] || colors.inactive
  }

  const getPlanColor = (plan: string) => {
    const colors = {
      free: 'text-gray-600 bg-gray-50',
      basic: 'text-blue-600 bg-blue-50',
      premium: 'text-purple-600 bg-purple-50',
      enterprise: 'text-indigo-600 bg-indigo-50',
    }
    return colors[plan as keyof typeof colors] || colors.free
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{family.name}</h2>
            <p className="text-gray-600">{family.slug}.housepoints.ai</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status and Plan */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="trial">Trial</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <div className={`mt-2 px-3 py-2 rounded-lg border ${getStatusColor(status)}`}>
                    <span className="text-sm font-medium">
                      Current: {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subscription Plan
                  </label>
                  <select
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  <div className={`mt-2 px-3 py-2 rounded-lg ${getPlanColor(plan)}`}>
                    <span className="text-sm font-medium">
                      Current: {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Family Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Users className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Members</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {family.member_count || 0}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Database className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Storage</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {family.storage_used_mb ? `${family.storage_used_mb} MB` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Activity className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Last Activity</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {family.last_activity_at
                          ? format(new Date(family.last_activity_at), 'MMM d')
                          : 'Never'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Database Info */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start space-x-3">
                  <Database className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-2">Database Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-blue-700 font-medium">Database:</span>
                        <span className="text-blue-900 ml-2">{family.db_name}</span>
                      </div>
                      {detail?.db_host && (
                        <div>
                          <span className="text-blue-700 font-medium">Host:</span>
                          <span className="text-blue-900 ml-2">
                            {detail.db_host}:{detail.db_port || 5432}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Family Members */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start space-x-3">
                  <Users className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-3">Family Members</h4>
                    {loadingMembers ? (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Loading members...</span>
                      </div>
                    ) : members.length === 0 ? (
                      <div className="text-sm text-gray-600">
                        No members found
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {members.map((member) => (
                          <div
                            key={member.id}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              member.is_parent ? 'bg-purple-50 border border-purple-100' : 'bg-white border border-gray-100'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                                member.is_parent ? 'bg-purple-500' : 'bg-blue-500'
                              }`}>
                                {member.display_name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {member.display_name}
                                  {member.is_parent && (
                                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                      Parent
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  @{member.username}
                                  {member.email && ` · ${member.email}`}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 text-xs">
                              {member.login_enabled ? (
                                <span className="text-green-600">Login enabled</span>
                              ) : (
                                <span className="text-gray-400">No login</span>
                              )}
                              {!member.is_active && (
                                <span className="text-red-500 bg-red-50 px-2 py-0.5 rounded">Inactive</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Subscription Info */}
              {(detail?.trial_ends_at || detail?.subscription_ends_at) && (
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-start space-x-3">
                    <Calendar className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-2">Subscription Details</h4>
                      <div className="space-y-1 text-sm">
                        {detail?.trial_ends_at && (
                          <div>
                            <span className="text-yellow-700 font-medium">Trial Ends:</span>
                            <span className="text-yellow-900 ml-2">
                              {format(new Date(detail.trial_ends_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        )}
                        {detail?.subscription_ends_at && (
                          <div>
                            <span className="text-yellow-700 font-medium">Subscription Ends:</span>
                            <span className="text-yellow-900 ml-2">
                              {format(new Date(detail.subscription_ends_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Timeline
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-900">
                      {format(new Date(family.created_at), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                  {family.updated_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Updated:</span>
                      <span className="text-gray-900">
                        {format(new Date(family.updated_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                  )}
                  {family.last_activity_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Activity:</span>
                      <span className="text-gray-900">
                        {format(new Date(family.last_activity_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Feature Toggles */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start space-x-3">
                  <Settings className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">Feature Toggles</h4>
                    {loadingFeatures ? (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Loading features...</span>
                      </div>
                    ) : features.length === 0 ? (
                      <div className="text-sm text-gray-600">
                        No feature toggles available for this family
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {features.map((feature) => (
                          <div key={feature.feature_key} className="flex items-start justify-between">
                            <label className="flex items-start space-x-3 flex-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={feature.enabled}
                                onChange={() => handleToggleFeature(feature.feature_key, feature.enabled)}
                                disabled={togglingFeature === feature.feature_key}
                                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {feature.display_name || feature.feature_key}
                                </div>
                                {feature.description && (
                                  <div className="text-xs text-gray-600 mt-0.5">
                                    {feature.description}
                                  </div>
                                )}
                              </div>
                            </label>
                            {togglingFeature === feature.feature_key && (
                              <Loader className="w-4 h-4 text-blue-500 animate-spin mt-1" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Warning for status changes */}
              {status === 'suspended' && status !== family.status && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">Warning</h4>
                      <p className="text-sm text-red-700">
                        Suspending this family will immediately block all access to their account.
                        Members will not be able to log in until the status is changed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {hasChanges && (
              <span className="flex items-center text-yellow-600">
                <AlertCircle className="w-4 h-4 mr-1" />
                You have unsaved changes
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
